import React, { useContext, useEffect, useMemo } from 'react'
import { SaveChartProps } from '../../../system-data-handler/types'
import { ChartView, getChartsEditingSettings, RechartData, useLegendState } from 'src/Libs/chart-kit'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import SystemChart from '../../shared/components/SystemChart'
import { useUserSettings } from 'src/Hooks'
import { getInitialProductionPieChart } from '../utils/initial-chart-config'
import useChartLayout from 'src/Libs/chart-kit/shared/hooks/use-chart-layout'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { DASHBOARD_POC_PRODUCTION_PIE_CHART_ID, DASHBOARD_PRODUCTION_PIE_CHART_ID } from 'src/Libs/qa/ids'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { UnitTypes } from 'src/Libs/types'
import { useTheme } from '@mui/material'

interface Props {
  data: RechartData
  view: ChartView
  resolution?: ChartView
  handleSaveChart: (props: SaveChartProps) => void
  from: Moment
  to: Moment
  showForecast: boolean
  height: number
  legendHeight: number
  setBiggestLegendHeight: (height: number) => void
}

/**
 * Pie Chart for production data
 */
const ProductionPieChart: React.FC<Props> = ({
  height,
  data,
  view,
  resolution,
  handleSaveChart,
  from,
  to,
  showForecast,
  legendHeight,
  setBiggestLegendHeight,
}) => {
  const theme = useTheme()
  const { t } = useCustomTranslation()
  const { userSettings } = useUserSettings()
  const initialChartLayout = useMemo(() => getInitialProductionPieChart(height), [height])
  const chartSettingsList = useMemo(() => getChartsEditingSettings(userSettings), [userSettings])
  const { chartLayout } = useChartLayout(chartSettingsList, initialChartLayout)
  const chartLayoutId = chartLayout.id ?? ''
  const { saveLegendState, hideLegend } = useLegendState(`${chartLayoutId}`)
  const { isDynamicPowerPriceEnabled } = useContext(DynamicPowerPricesEnabledContext)
  const [customLayout, setCustomLayout] = React.useState(chartLayout)
  const dataQa = data.isPocChart ? DASHBOARD_POC_PRODUCTION_PIE_CHART_ID : DASHBOARD_PRODUCTION_PIE_CHART_ID

  useEffect(() => {
    const updatedTraces = chartLayout.traces.map(trace => {
      if (trace.key === 'SelfProduction') {
        return { ...trace, color: theme.palette.productionPieChart.SelfProduction }
      } else if (trace.key === 'NetIn') {
        return { ...trace, color: theme.palette.productionPieChart.NetIn }
      }

      return trace
    })

    setCustomLayout({ ...chartLayout, traces: updatedTraces })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartLayout])

  return (
    <SystemChart
      view={view}
      resolution={resolution}
      from={from}
      to={to}
      showForecast={showForecast}
      loadingState={data.loadingState}
      rechartData={data}
      customLayout={customLayout}
      handleSaveChart={props => handleSaveChart({ customLayout, chartSettings: props })}
      maxHeight={customLayout.maxHeight}
      isDynamicPowerPriceEnabled={isDynamicPowerPriceEnabled}
      hideLegend={hideLegend}
      toggleHideLegend={() => saveLegendState()}
      dataQa={dataQa}
      setBiggestLegendHeight={setBiggestLegendHeight}
      fixedLegendHeight={legendHeight}
      pieChartTooltipText={t('system-charts.production-pie-chart.tooltip')}
      legendUnitType={UnitTypes.wattHour}
    />
  )
}

export default ProductionPieChart
